import styles from './index.module.css'

import * as React from 'react'

import type { ParentProps } from 'types/explorer'
import type { Track } from 'types/track'

import { Badge, SegmentedControl, Text } from 'components/ds'
import Similarity from 'components/explorer/pane/similarity'

type ContentProps = {
  simSearchProps?: ParentProps
  track?: Track
  playlistNum?: number
  list?: React.ReactNode
}
type TrackProps = {
  alias: string
  label: string
  isSimilarTrack?: boolean
  isAggregatedTrack?: boolean
  content?: ContentProps
}
type TabProps = {
  index: number
  onChange: (index: number) => void
  tabs: Array<TrackProps>
}

/**
 * Related-content switcher for the track panel ("Also appears on" /
 * "Similar tracks") rendered as a segmented control whose active segment
 * carries a count badge for its list. Contents stay mounted across switches
 * so similar tracks are only fetched once.
 */
const TrackTabs = ({ tabs, index, onChange }: TabProps): React.ReactNode => {
  const [similarCount, setSimilarCount] = React.useState<number | undefined>(
    undefined,
  )

  const visibleTabs = tabs.filter(tab => tab.content)
  const activeTab = tabs[index]?.content ? tabs[index] : visibleTabs[0]
  if (!activeTab) return null

  const getCount = (tab: TrackProps): number | undefined =>
    tab.isSimilarTrack ? similarCount : tab.content?.playlistNum

  const renderLabel = (tab: TrackProps, isActive: boolean) => {
    const count = getCount(tab)
    return (
      <span className={styles.segmentLabel}>
        {tab.label}
        {isActive && count !== undefined && (
          <Badge color="violet" variant="soft" radius="full">
            {count}
          </Badge>
        )}
      </span>
    )
  }

  const renderContent = (tab: TrackProps) => {
    const { content } = tab
    if (!content) return null

    if (tab.isSimilarTrack && content.track?.id && content.simSearchProps) {
      return (
        <Similarity
          parentProps={content.simSearchProps}
          trackId={content.track.id}
          aggregatedTrackURL={
            tab.isAggregatedTrack
              ? content.track?.public_download_url2
              : undefined
          }
          onResultsCount={setSimilarCount}
        />
      )
    }

    return <div className="ExplorerPane_related-cont">{content.list}</div>
  }

  return (
    <div>
      <div className={styles.header}>
        {visibleTabs.length > 1 ? (
          <SegmentedControl.Root
            className={styles.control}
            size="1"
            radius="full"
            value={activeTab.alias}
            onValueChange={value => {
              const nextIndex = tabs.findIndex(tab => tab.alias === value)
              if (nextIndex >= 0 && nextIndex !== index) onChange(nextIndex)
            }}
          >
            {visibleTabs.map(tab => (
              <SegmentedControl.Item key={tab.alias} value={tab.alias}>
                {renderLabel(tab, tab.alias === activeTab.alias)}
              </SegmentedControl.Item>
            ))}
          </SegmentedControl.Root>
        ) : (
          <Text size="2" weight="medium">
            {renderLabel(activeTab, true)}
          </Text>
        )}
      </div>
      {visibleTabs.map(tab => (
        <div
          key={tab.alias}
          className={tab.alias === activeTab.alias ? undefined : styles.hidden}
        >
          {renderContent(tab)}
        </div>
      ))}
    </div>
  )
}

export default TrackTabs
